import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, ShieldAlert, Download, ExternalLink, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Page publique de vérification d'authenticité d'un contrat Nold Go (scan QR).
// Accessible sans authentification à /verify/contract/:id
export default function PublicContractVerify() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/verify/contract/${encodeURIComponent(id)}`);
        if (alive) setData(res.data);
      } catch {
        if (alive) setData({ valid: false, id });
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [id]);

  useEffect(() => {
    document.title = "Nold Go — Vérification de contrat";
  }, []);

  return (
    <div data-testid="public-verify-contract" className="min-h-screen bg-[#FDF8F0]">
      <header className="border-b border-[#E2DFD8]">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2">
            <span className="font-head font-black text-2xl text-[#F26419]">Nold <span className="text-[#1A1A1A]">Go</span></span>
          </Link>
          <span className="text-xs text-[#75807A] font-semibold uppercase tracking-wider">
            Vérification publique
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        {loading ? (
          <div className="flex items-center justify-center py-24" data-testid="public-verify-loading">
            <Loader2 className="w-8 h-8 animate-spin text-[#F26419]" />
          </div>
        ) : data?.valid ? <ValidCard data={data} /> : <InvalidCard id={id} />}

        <footer className="mt-14 text-center text-xs text-[#75807A] leading-relaxed">
          Nold Go SRL · Rue Jourdan 71 A0 · 1060 Saint-Gilles · Bruxelles · Belgique<br />
          TVA BE 1040.580.267 · <a href="https://www.noldgo.com" className="text-[#F26419] hover:underline">www.noldgo.com</a>
        </footer>
      </main>
    </div>
  );
}

function ValidCard({ data }) {
  const signed = new Date(data.signed_at).toLocaleString("fr-BE",
    { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const pdfUrl = `${API}/verify/contract/${encodeURIComponent(data.id)}.pdf`;
  const isDriver = data.party_type === "driver";
  return (
    <div data-testid="public-verify-valid" className="rounded-3xl bg-white border-2 border-[#16a34a]/40 p-6 sm:p-8 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-14 h-14 rounded-2xl grid place-items-center bg-[#DCFCE7]">
          <ShieldCheck className="w-8 h-8 text-[#16a34a]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="inline-flex items-center rounded-full text-[10px] font-bold px-2 py-0.5 mb-2"
               style={{ background: isDriver ? "#DCFCE7" : "#FFF3E6", color: isDriver ? "#166534" : "#7A3A00" }}>
            {isDriver ? "🚴 CONTRAT LIVREUR VÉLO" : "🍔 CONTRAT RESTAURATEUR"}
          </div>
          <h1 className="font-head font-black text-2xl sm:text-3xl text-[#1A1A1A]">
            Contrat authentique ✅
          </h1>
          <p className="text-sm text-[#75807A] mt-1">
            Ce contrat a bien été signé électroniquement via la plateforme Nold Go.
          </p>
        </div>
      </div>

      <dl className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5 text-sm">
        <Info label="Émis par" value={data.issuer} sub={`TVA ${data.issuer_vat}`} />
        <Info
          label={isDriver ? "Livreur" : "Signé par"}
          value={data.business_name}
          sub={data.vat_number ? `TVA ${data.vat_number}` : (data.phone || null)}
        />
        <Info label="Date de signature" value={signed} />
        <Info label="Ville" value={data.city || "Bruxelles"} />
        <Info label="Signataire" value={data.signatory_name || "—"} />
        <Info label="Identifiant du contrat" value={<span className="font-mono text-[#F26419]">{data.hash}</span>} sub={<span className="font-mono text-[11px] text-[#75807A]">{data.id}</span>} />
      </dl>

      <div className="mt-8 flex flex-wrap gap-3">
        <a
          data-testid="public-verify-download-pdf"
          href={pdfUrl} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#F26419] hover:bg-[#D94D0E] text-white font-bold text-sm">
          <Download className="w-4 h-4" /> Télécharger le contrat signé (PDF)
        </a>
        <a
          href="https://www.noldgo.com"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white border-2 border-[#E2DFD8] hover:border-[#F26419] text-[#1A1A1A] font-bold text-sm">
          <ExternalLink className="w-4 h-4" /> Visiter noldgo.com
        </a>
      </div>
    </div>
  );
}

function InvalidCard({ id }) {
  return (
    <div data-testid="public-verify-invalid" className="rounded-3xl bg-white border-2 border-[#D32F2F]/30 p-6 sm:p-8 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-14 h-14 rounded-2xl grid place-items-center bg-[#FEE2E2]">
          <ShieldAlert className="w-8 h-8 text-[#D32F2F]" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="font-head font-black text-2xl sm:text-3xl text-[#1A1A1A]">
            Contrat introuvable
          </h1>
          <p className="text-sm text-[#75807A] mt-1">
            Aucun contrat signé n'existe avec cet identifiant. Il peut avoir été copié incorrectement ou ne pas provenir de Nold Go.
          </p>
          <p className="mt-4 text-xs text-[#75807A] font-mono break-all">ID : {id}</p>
          <div className="mt-5">
            <a href="mailto:nold.contact@noldgo.com"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#1A1A1A] hover:bg-black text-white font-bold text-sm">
              Signaler à Nold Go
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value, sub }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-[#75807A] font-bold">{label}</dt>
      <dd className="mt-1 text-[#1A1A1A] font-semibold break-words">{value}</dd>
      {sub && <div className="text-xs text-[#75807A] mt-0.5">{sub}</div>}
    </div>
  );
}
